(() => {
  'use strict';
  let active = 0;
  const waiting = [];
  const pump = () => {
    while (active < 2 && waiting.length) {
      const job = waiting.shift();
      job.signal.removeEventListener('abort', job.cancel);
      if (job.signal.aborted) { job.reject(new DOMException('Aborted', 'AbortError')); continue; }
      active++;
      job.resolve();
    }
  };
  const request = async (url, signal) => {
    await new Promise((resolve, reject) => {
      const job = {resolve, reject, signal};
      job.cancel = () => {
        const index = waiting.indexOf(job);
        if (index >= 0) waiting.splice(index, 1);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal.aborted) { job.cancel(); return; }
      signal.addEventListener('abort', job.cancel, {once: true});
      waiting.push(job); pump();
    });
    try {
      const response = await fetch(url, {signal, credentials: 'same-origin', priority: 'low'});
      if (!response.ok) throw new Error('Collection request failed');
      return await response.text();
    } finally { active--; pump(); }
  };
  class SkyProductFeed extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); this.seen ??= new Set(); this.pages ??= new Set(); this.pending ??= [];
      this.grid = this.querySelector('[data-sky-grid]'); this.status = this.querySelector('[data-feed-status]');
      this.button = this.querySelector('[data-feed-more]'); this.next ??= this.dataset.url; this.busy = false;
      this.button.addEventListener('click', () => this.load(), {signal: this.abort.signal});
      this.controls = this.querySelector('[data-feed-controls]');
      this.previous = this.querySelector('[data-feed-prev]'); this.forward = this.querySelector('[data-feed-next]');
      this.previous?.addEventListener('click', () => this.scrollProducts(-1), {signal: this.abort.signal});
      this.forward?.addEventListener('click', () => this.scrollProducts(1), {signal: this.abort.signal});
      this.grid.addEventListener('scroll', () => this.scheduleSwipeControls(), {passive:true, signal:this.abort.signal});
      if ('ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(() => this.scheduleSwipeControls()); this.resizeObserver.observe(this.grid); }
      this.updateSwipeControls();
      this.button.hidden = !this.next && !this.pending.length;
      this.button.disabled = false; this.grid.setAttribute('aria-busy', 'false');
      // A DOM move in the editor reconnects the same element; keep its products.
      if (this.seen.size) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
        }, {rootMargin: '250px'});
        this.observer.observe(this);
      } else this.load();
    }
    async load() {
      if (this.busy || (!this.next && !this.pending.length)) return;
      const lifecycle = this.abort;
      this.busy = true; this.button.disabled = true; this.grid.setAttribute('aria-busy', 'true');
      const target = this.seen.size + Math.max(2, Math.min(200, Number(this.dataset.limit) || 200));
      try {
        while ((this.next || this.pending.length) && this.seen.size < target) {
          if (lifecycle.signal.aborted) break;
          if (this.pending.length) {
            const fragment = document.createDocumentFragment();
            const batch = this.pending.splice(0, Math.min(12, target - this.seen.size));
            batch.forEach(card => {
              this.seen.add(card.dataset.skyProduct);
              const imported = document.importNode(card, true);
              this.configureCard(imported);
              // The same product can appear in several independently editable sections.
              const prefix = `sky-${this.dataset.section}-`;
              const ids = new Map();
              [imported, ...imported.querySelectorAll('[id]')].forEach(el => {
                // A product form contains an input named "id", which shadows form.id.
                const id = el.getAttribute('id');
                if (id) { ids.set(id, prefix + id); el.setAttribute('id', prefix + id); }
              });
              [imported, ...imported.querySelectorAll('*')].forEach(el => {
                ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attr => {
                  if (el.hasAttribute(attr)) el.setAttribute(attr, el.getAttribute(attr).split(/\s+/).map(id => ids.get(id) || id).join(' '));
                });
              });
              fragment.append(imported);
            });
            this.grid.append(fragment);
            this.scheduleSwipeControls();
            // Let taps and scrolling run between batches in large product sections.
            await new Promise(resolve => setTimeout(resolve, 0));
            continue;
          }
          const url = new URL(this.next, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid collection origin');
          url.searchParams.set('section_id', this.dataset.feedSection === 'sky-bestsellers-feed' ? 'sky-bestsellers-feed' : 'sky-collection-feed');
          const original = new URL(this.dataset.url, location.origin);
          if (original.searchParams.has('sort_by')) url.searchParams.set('sort_by', original.searchParams.get('sort_by'));
          if (this.pages.has(url.href)) throw new Error('Repeated collection page');
          this.status.textContent = window.skyStrings.loading;
          const html = await request(url.href, lifecycle.signal);
          if (lifecycle.signal.aborted) break;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector('[data-sky-server-grid]');
          if (!source) throw new Error('Missing collection response');
          const cards = [...source.querySelectorAll('[data-sky-product]')];
          const fresh = cards.filter(card => !this.seen.has(card.dataset.skyProduct));
          if (cards.length && !fresh.length) throw new Error('Collection did not advance');
          this.pending.push(...fresh); this.pages.add(url.href); this.next = source.dataset.next || '';
          this.status.textContent = window.skyStrings.count.replace('[count]', this.seen.size);
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        if (this.abort === lifecycle) this.status.textContent = this.seen.size ? window.skyStrings.count.replace('[count]', this.seen.size) : window.skyStrings.empty;
      } catch (error) {
        if (error.name !== 'AbortError' && this.abort === lifecycle) this.status.textContent = window.skyStrings.error;
      } finally {
        if (this.abort === lifecycle) {
          this.busy = false; this.button.disabled = false; this.button.hidden = !this.next && !this.pending.length;
          this.grid.setAttribute('aria-busy', 'false');
        }
      }
    }
    scrollProducts(direction) {
      const rtl = getComputedStyle(this.grid).direction === 'rtl' ? -1 : 1;
      this.grid.scrollBy({left: direction * rtl * this.grid.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
    configureCard(card) {
      if (!card.classList.contains('sky-native-card')) return;
      const ratio = this.dataset.cardRatio;
      if (ratio === 'square' || ratio === 'portrait') {
        card.querySelectorAll('.card, .card__inner').forEach(el => el.style.setProperty('--ratio-percent', ratio === 'portrait' ? '125%' : '100%'));
      }
      if (this.dataset.secondaryImage !== 'true') card.querySelectorAll('.card__media .media > img:not(:first-child)').forEach(el => el.remove());
      if (this.dataset.showVendor !== 'true') card.querySelectorAll('[data-sky-card-vendor]').forEach(el => el.remove());
      if (this.dataset.showRating !== 'true') card.querySelectorAll('.rating,.rating-text,.rating-count').forEach(el => el.remove());
      if (this.dataset.quickAdd === 'false') card.querySelectorAll('.quick-add').forEach(el => el.remove());
    }
    scheduleSwipeControls() {
      if (this.swipeFrame) return;
      this.swipeFrame = requestAnimationFrame(() => {this.swipeFrame = 0;if (this.isConnected) this.updateSwipeControls();});
    }
    updateSwipeControls() {
      if (!this.controls) return;
      const maximum = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
      this.controls.hidden = this.dataset.swipeMobile !== 'true' || !matchMedia('(max-width:749px)').matches || maximum < 2;
      const position = Math.min(maximum, Math.abs(this.grid.scrollLeft));
      this.previous.disabled = position < 2; this.forward.disabled = position >= maximum - 2;
    }
    disconnectedCallback() { cancelAnimationFrame(this.swipeFrame);this.swipeFrame = 0;this.observer?.disconnect(); this.resizeObserver?.disconnect(); this.abort?.abort(); }
  }
  if (!customElements.get('sky-product-feed')) customElements.define('sky-product-feed', SkyProductFeed);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.sky-language[open]').forEach(el => { el.open = false; el.querySelector('summary').focus(); });
  });
  document.addEventListener('click', event => document.querySelectorAll('.sky-language[open]').forEach(el => { if (!el.contains(event.target)) el.open = false; }));
})();
